import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDownUp } from "lucide-react";
import { Dropdown } from "components/common/Dropdown";
import type { DropdownOption } from "components/common/Dropdown/types";
import type { SidebarSortMode } from "../utils/sortSidebarItems";
import type { SidebarSortControlProps } from "./types";

/** Full, descriptive labels shown inside the dropdown menu. */
const SORT_MODE_LABEL_KEYS: Record<SidebarSortMode, string> = {
  default: "smartSurveyor.reviewPage.sidebarSort.default",
  newest: "smartSurveyor.reviewPage.sidebarSort.newest",
  oldest: "smartSurveyor.reviewPage.sidebarSort.oldest",
};

/** Short value shown in the trigger after the constant "Sort:" prefix. */
const SORT_MODE_SHORT_LABEL_KEYS: Record<SidebarSortMode, string> = {
  default: "smartSurveyor.reviewPage.sidebarSort.defaultShort",
  newest: "smartSurveyor.reviewPage.sidebarSort.newestShort",
  oldest: "smartSurveyor.reviewPage.sidebarSort.oldestShort",
};

const SORT_MODES: SidebarSortMode[] = ["default", "newest", "oldest"];

export const SidebarSortControl = ({
  value,
  onChange,
}: SidebarSortControlProps) => {
  const { t } = useTranslation();

  const options: DropdownOption<SidebarSortMode>[] = useMemo(
    () =>
      SORT_MODES.map((mode) => ({
        value: mode,
        label: t(SORT_MODE_LABEL_KEYS[mode]),
        triggerLabel: (
          <span className="flex items-center gap-2 whitespace-nowrap">
            <ArrowDownUp className="w-3 h-3 shrink-0 text-gray-500" />
            <span>
              {`${t("smartSurveyor.reviewPage.sidebarSort.label")}: `}
              <span className="inline-block w-14 text-left text-gray-900">
                {t(SORT_MODE_SHORT_LABEL_KEYS[mode])}
              </span>
            </span>
          </span>
        ),
      })),
    [t],
  );

  return (
    <Dropdown<SidebarSortMode>
      options={options}
      value={value}
      onChange={onChange}
      className="w-full"
      buttonClassName="w-full justify-between px-3 py-2 border-gray-300"
    />
  );
};
